import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import {
  Segment, Dimmer, Loader, Message, Header, Label,
  Grid, GridColumn, Divider, Button, Table,
} from "semantic-ui-react";
import { fetchVodafoneOrders } from "../libs/requests/vodafoneOrderSearch";
import Panel from "./Panel";
import moment from "moment";

const STATUS_COLOUR = {
  Pending: "yellow", Accepted: "olive", Committed: "teal",
  Completed: "green", Rejected: "red", Cancelled: "grey", Failed: "red",
};

function Field({ label, value }) {
  if (value == null || value === "") return null;
  return (
    <GridColumn>
      <Grid columns={2}>
        <GridColumn><strong>{label}</strong></GridColumn>
        <GridColumn>{value}</GridColumn>
      </Grid>
    </GridColumn>
  );
}

function SectionDivider() {
  return <GridColumn width={16}><Divider /></GridColumn>;
}

function parseUpdate(message) {
  if (!message) return {};
  try { return JSON.parse(message); } catch { return { raw: message }; }
}

export default function ViewVodafoneOrder() {
  const { reference } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchVodafoneOrders(reference)
      .then(results => {
        const match = Array.isArray(results)
          ? results.find(o => o.reference?.toLowerCase() === reference.toLowerCase())
          : null;
        if (match) setOrder(match);
        else setError(`No Vodafone order found for reference: ${reference}`);
      })
      .catch(() => setError("Failed to load order details."))
      .finally(() => setLoading(false));
  }, [reference]);

  if (loading) {
    return (
      <Segment>
        <Dimmer active inverted><Loader>Loading order…</Loader></Dimmer>
      </Segment>
    );
  }

  if (error) {
    return <Message negative><Message.Header>{error}</Message.Header></Message>;
  }

  const header = (
    <Header as="h2" textAlign="center" style={{
      margin: "20px 0", color: "black", zIndex: 1000,
      backgroundColor: "rgba(255, 255, 255, 0.8)", padding: "10px", borderRadius: "5px",
    }}>
      Vodafone Order — {order.reference}
      <Header.Subheader style={{ color: "black" }}>
        <Label color={STATUS_COLOUR[order.status] ?? "grey"} style={{ marginTop: "6px" }}>
          {order.status}
        </Label>
      </Header.Subheader>
    </Header>
  );

  const hasAppointment = order.appointmentDate || order.appointmentReference || order.appointmentTimeSlot;

  return (
    <>
      <Segment padded>
        <div style={{ marginBottom: "20px" }}>
          <Panel header={header}>
            <Grid columns={3}>
              <Field label="Reference"         value={order.reference} />
              <Field label="Ordered By"        value={order.orderedBy} />
              <SectionDivider />
              <Field label="Product"           value={order.productDescription} />
              <Field label="Product Code"      value={order.productCode} />
              <Field label="Feature Code"      value={order.featureCode} />
              <Field label="Care Level"        value={order.careLevel} />
              <Field label="Order Type"        value={order.orderType} />
              <SectionDivider />
              <Field label="Address"           value={order.address} />
              <Field label="Activation Date"
                value={order.activationDate ? moment(order.activationDate).format("DD/MM/YYYY") : null} />
              <SectionDivider />
              <Field label="Contact Name"      value={order.contactName} />
              <Field label="Contact Phone"     value={order.contactPhone} />
            </Grid>
          </Panel>
        </div>

        {hasAppointment && (
          <div style={{ marginBottom: "20px" }}>
            <Panel header="Appointment Details">
              <Grid columns={3}>
                <Field label="Appointment Date"
                  value={order.appointmentDate ? moment(order.appointmentDate).format("DD/MM/YYYY") : null} />
                <Field label="Appointment Slot"      value={order.appointmentTimeSlot} />
                <Field label="Appointment Reference" value={order.appointmentReference} />
                <Field label="Appointment Type"      value={order.appointmentType} />
              </Grid>
            </Panel>
          </div>
        )}

        {order.updates?.length > 0 && (
          <Panel header="Order Updates">
            <Table celled>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Date</Table.HeaderCell>
                  <Table.HeaderCell>Status</Table.HeaderCell>
                  <Table.HeaderCell>Message</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {order.updates.map((u, i) => {
                  const parsed = parseUpdate(u.message);
                  const ts = parsed.received_date_time || u.timestamp;
                  const dateStr = ts ? moment(ts).format("DD/MM/YYYY HH:mm") : "—";
                  const statusStr = parsed.order_status ?? "—";
                  const msg = parsed.status_message || parsed.raw || u.message || "—";
                  return (
                    <Table.Row key={i}>
                      <Table.Cell collapsing>{dateStr}</Table.Cell>
                      <Table.Cell collapsing>{statusStr}</Table.Cell>
                      <Table.Cell>{msg}</Table.Cell>
                    </Table.Row>
                  );
                })}
              </Table.Body>
            </Table>
          </Panel>
        )}
      </Segment>

      <Link to="/LandingPage">
        <Button primary className="button-primary" style={{ marginTop: "20px" }}>
          Back
        </Button>
      </Link>
    </>
  );
}
